
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Phone } from 'lucide-react';
import { companyInfo, beforeAfterProjects } from '../data';
import BeforeAfterSlider from '../components/BeforeAfterSlider';

const BeforeAfter = () => {
  return (
    <div className="pt-24 min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#1b4c2e] text-white py-16 md:py-24 relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h3 className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-green-400 mb-4">
              Resultater der taler for sig selv
            </h3>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
              Før & <span className="text-green-400">Efter</span> Galleri
            </h1>
            <p className="text-lg md:text-xl text-green-50/80 leading-relaxed max-w-2xl">
              Oplev den utrolige forskel vores professionelle behandlinger gør. Træk i skyderen på billederne for at se forvandlingen.
            </p>
          </div>
        </div>
        {/* Background Decor */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-[#245e39] skew-x-12 translate-x-1/2 opacity-20" />
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {beforeAfterProjects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100 flex flex-col"
              >
                {/* Slider Container */}
                <div className="aspect-[4/3] relative group">
                  <BeforeAfterSlider 
                    beforeImage={project.before}
                    afterImage={project.after}
                    beforeLabel="Før"
                    afterLabel="Efter"
                  />
                  <div className="absolute top-4 left-4 z-20">
                    <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                      <Sparkles size={14} className="text-green-600" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-900">Interaktiv</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Info Box */}
          <div className="mt-16 bg-green-900 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="max-w-xl">
                <h3 className="text-2xl md:text-3xl font-black mb-4">Skal vi forvandle din have?</h3>
                <p className="text-green-50/80 text-lg">
                  Vi tager løbende nye billeder af vores projekter. Kontakt os i dag for en uforpligtende snak om dit næste haveprojekt.
                </p>
              </div>
              <Link
                to="/kontakt"
                className="bg-white text-green-900 px-8 py-4 rounded-xl font-bold hover:bg-green-50 transition-all shadow-xl whitespace-nowrap"
              >
                Kontakt os nu
              </Link>
            </div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
              Vil du se din egen have forvandlet?
            </h2>
            <p className="text-gray-600 mb-10 text-lg">
              Vi kommer gerne forbi og giver et uforpligtende tilbud på din opgave.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/kontakt"
                className="bg-green-600 text-white px-8 py-3.5 rounded-xl font-bold hover:bg-green-700 transition-all shadow-lg shadow-[#1b4c2e]/10 w-[289px] sm:w-fit text-center"
              >
                Bestil et tilbud
              </Link>
              <a
                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                className="bg-gray-100 text-gray-900 px-8 py-3.5 rounded-xl font-bold hover:bg-gray-200 transition-all w-[289px] sm:w-fit text-center"
              >
                <Phone size={18} className="inline mr-2" />
                Ring til os
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BeforeAfter;
